<script setup lang="ts">
    import { DEFAULT_IMG } from '@/constants/image';
    import { formatCurrency, totalHours } from '@/helpers/formatting';
    import { type PackageTyping } from '@/typings/widget';
    import AdvancedImage from '@shared/components/ui/advanced-image.vue';

    const { t, locale } = useI18n();

    const props = defineProps<{
        package: PackageTyping
    }>();

    const sortedEventTypes = computed(() => [...props.package.eventTypes].sort((itemA, itemB) => itemA.name.localeCompare(itemB.name)));
</script>

<template>
    <div class="package-item">
        <div class="package-item__image">
            <AdvancedImage
                :media="package.cover"
                :preview="package?.cover ? null : DEFAULT_IMG"
                :asset-width="500"
                no-crop
                aspect-ratio="2:1"
            />
            <div class="item-description-hours">
                {{ totalHours(props.package?.programme) }}
            </div>
        </div>
        <div class="package-item__text">
            <div class="content">
                <div class="title">
                    {{ props.package.name }}<br>
                </div>
                <div class="description">
                    <span
                        v-for="eventType in sortedEventTypes"
                        :key="eventType.id"
                    >
                        {{ eventType.name }}
                    </span>
                </div>
            </div>
            <div class="price">
                <span>
                    {{ t('widget.global.from') }} {{ formatCurrency(props.package?.pricePerPerson, props.package?.currency, locale) }}
                </span>
                {{ t('widget.global.guest') }}
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.package-item {
    position: relative;
    display: flex;
    overflow: hidden;
    flex-direction: column;
    padding: 0;
    border: 1px solid var(--border-tertiary);
    border-radius: var(--border-radius-sm);
    background: var(--background-secondary);
    block-size: 100%;
    cursor: pointer;
    inline-size: 100%;
    transition: all 0.2s ease;

    @include min-width(md) {
        max-block-size: 345px;
    }

    .package-item__image {
        position: relative;
        flex: 1;
        max-block-size: 200px;
    }

    .package-item__text {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: space-between;
        padding: var(--spacing-03);
        border-radius: 0 0 var(--border-radius-sm) var(--border-radius-sm);

        .title {
            font-size: 16px;
            font-weight: 500;
        }

        .description {
            display: flex;
            flex-wrap: wrap;
            gap: var(--spacing-base);
            margin-block-start: 8px;

            span {
                display: flex;
                color: var(--text-secondary);
                font-size: 14px;

                &:not(:last-child)::after {
                    content: ',';
                }
            }
        }
    }

    .price {
        font-size: 14px;
        font-weight: 300;
        margin-block-start: 32px;

        /* stylelint-disable-next-line no-descending-specificity */
        span {
            font-size: 16px;
            font-weight: 500;
        }
    }

    .item-description-hours {
        position: absolute;
        display: inline-block;
        border-radius: 5px;
        background-color: var(--background-primary);
        font-size: 12px;
        inset-block-end: 16px;
        inset-inline-end: 16px;
        padding-block: 5px;
        padding-inline: 8px;
    }

    &:hover {
        background: var(--background-secondary-highlight);
        transform: translateY(-2px);
    }
}
</style>
